import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { MessageService } from 'primeng/api';
import { BehaviorSubject } from 'rxjs';
import { User } from '../models/user';
import { AppStateService } from '../shared/app-state.service';
import { BaseService } from '../shared/base.service';
import * as settingsActions from '../shared/state/settings.actions';
import * as appAction from '../state/app.actions';
import { AppState } from '../state/app.state';


export class LoginResponse {
  message = '';
  token = '';
  user = new User;
}

/**
 * Owns authentication state and is the single source of truth for
 * login/logout transitions. All NgRx dispatches related to authentication
 * (ToggleCurrentUser, settings Load/Reset) happen here, not in UI
 * components. This ensures state management works regardless of which
 * components are mounted — e.g. when navigating directly to a guarded
 * route on a fresh tab, `LoginComponent` is never constructed but
 * `LoginService` (injected by `AuthGuardService`) still restores state
 * and triggers the settings load.
 */
@Injectable({ providedIn: 'root' })
export class LoginService extends BaseService {
  currentUser: User;
  redirectUrl = 'home';
  private _isAdmin = false;
  private _isEditor = false;
  isLoggedIn = false;
  isLoggedIn$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  token = '';

  constructor(http: HttpClient, messageService: MessageService, appState: AppStateService,
    private router: Router, store: Store<AppState>) {
    super(http, messageService, appState, store);

    this.appState.forcedLogOut$.subscribe((status) => {
      if (status) { this.logout(); }
    });
  }

  get isAdmin(): boolean { return this._isAdmin; }
  get isEditor(): boolean { return this._isEditor; }

  /**
   * Restore auth state from `localStorage` and put it into the NgRx store.
   * Called from `APP_INITIALIZER` in `AppModule` (for fresh-tab restore).
   * Running this through `APP_INITIALIZER` guarantees it executes after
   * all modules are loaded and all effects (including `SettingsEffects`)
   * are registered — so the dispatched `Load` action is reliably caught.
   */
  public restoreFromStorage(): void {
    const token = this.getToken();
    if (!token) { return; }

    const stored = localStorage.getItem('user');
    if (!stored) { return; }

    let parsed: any;
    try {
      parsed = JSON.parse(stored);
    } catch {
      // Corrupted JSON — wipe and bail.
      localStorage.removeItem('user');
      return;
    }

    this.currentUser = Object.assign(new User(), parsed);
    if (!this.currentUser.id) { return; }

    this.isLoggedIn = true;
    this._isAdmin = this.currentUser.roles.some(role =>
      (role.name === 'superadministrator') || (role.name === 'administrator'));
    this._isEditor = this.currentUser.roles.some(role => role.name === 'editor');

    // Persist auth state in NgRx so selectors see the same user as
    // LoginService.
    this.store.dispatch(new appAction.ToggleCurrentUser(this.currentUser));

    // Trigger the settings load directly. `loadSettings$` picks this up
    // and fires the HTTP request; the token is in localStorage at this
    // point so the auth interceptor will add the `Authorization` header.
    this.store.dispatch(new settingsActions.Load());

    this.isLoggedIn$.next(this.isLoggedIn);
  }

  login(user, password) {
    const loginUrl = this.apiUrl + '/login';
    this.http.post<LoginResponse>(loginUrl, { 'email': user, 'password': password }, this.httpOptions)
      .subscribe(
        (response) => {
          if (response.message === 'success') {
            this.isLoggedIn = true;
            this.token = response.token;
            this.currentUser = response.user;

            this._isAdmin = response.user.roles.some(role =>
              ((role.name === 'superadministrator') || (role.name === 'administrator')));
            this._isEditor = this.currentUser.roles.some(role =>
              role.name === 'editor');

            // Persist to localStorage FIRST so the auth interceptor sees
            // the token on the very next HTTP request (the settings load
            // dispatched below).
            localStorage.setItem('token', this.token);
            localStorage.setItem('user', JSON.stringify(this.currentUser));

            // Update NgRx store with the authenticated user. Previously this
            // happened in `LoginComponent.ngOnInit` via the `isLoggedIn$`
            // subscription — state management now lives entirely in the
            // service so it works even when LoginComponent is not mounted.
            this.store.dispatch(new appAction.ToggleCurrentUser(this.currentUser));

            // Trigger settings load directly.
            this.store.dispatch(new settingsActions.Load());

            this.isLoggedIn$.next(this.isLoggedIn);
            this.router.navigate([this.redirectUrl]);
          }
        },
        (error: HttpErrorResponse) => {
          this.messageService.add({
            severity: 'error',
            summary: 'User login failed!',
            detail: 'Bitte prüfen Sie Emailadresse und Passwort!'
          });
          this.isLoggedIn = false;
          localStorage.removeItem('token');
          this.isLoggedIn$.next(this.isLoggedIn);
        });
  }

  public getToken(): string | null {
    return localStorage.getItem('token');
  }

  logout() {
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    this._isAdmin = false;
    this._isEditor = false;
    this.currentUser = null;
    this.isLoggedIn = false;
    this.store.dispatch(new settingsActions.Reset());
    this.isLoggedIn$.next(this.isLoggedIn);
    this.router.navigate(['/']);
  }

}
